/**
 * 3D backlit "HIGH ROLLERS" sign mounted on the back wall behind the dealer.
 *
 * Replaces the DOM `LeaderboardSign` div. Built from a dark neon panel + a gold
 * emissive frame (emissive > 1 so it BLOOMS through the selective bloom pass) +
 * troika `<Text>` for the marquee title and the live standings. Bound to the
 * Zustand `leaderboard` slice (top balances, derived server-side).
 *
 * troika `<Text>` is used (not `<Text3D>`) because it needs NO external font-JSON
 * asset — it ships a sane SDF fallback font, so the sign always renders even
 * before any asset is delivered (Fix-4 guarding requirement).
 *
 * Draw-call budget: panel(1) + frame(1) + backlight bar(1) + a handful of text
 * meshes (troika batches glyphs per <Text>) ≈ under 10 for the whole sign.
 */

import { useMemo } from 'react';
import { Text } from '@react-three/drei';
import { useGameStore } from '../state/store';
import { palette, materials } from '../theme/tokens';
import { formatCents } from '../util/format';
import { FLAT_Z } from './seating';

/** Sign placement on the back wall (matches Room.tsx backZ). */
const SIGN = {
  z: FLAT_Z - 5.42, // a hair in front of the back wall plane
  y: 3.35,
  width: 6.2,
  // Tall enough for the title + all 5 rows with a clear bottom margin (the old
  // 2.4 clipped row 5 below the panel edge).
  height: 3.0,
} as const;

export function HighRollersSign() {
  const leaderboard = useGameStore((s) => s.server.leaderboard);

  const rows = useMemo(() => leaderboard.slice(0, 5), [leaderboard]);

  return (
    <group position={[0, SIGN.y, SIGN.z]}>
      {/* Dark neon backing panel. */}
      <mesh position={[0, 0, -0.04]}>
        <planeGeometry args={[SIGN.width, SIGN.height]} />
        <meshStandardMaterial color={palette.feltDark} roughness={0.6} metalness={0.2} />
      </mesh>

      {/* Gold emissive frame (blooms). Four thin bars around the panel. */}
      <FrameBar x={0} y={SIGN.height / 2} w={SIGN.width + 0.18} h={0.1} />
      <FrameBar x={0} y={-SIGN.height / 2} w={SIGN.width + 0.18} h={0.1} />
      <FrameBar x={-SIGN.width / 2} y={0} w={0.1} h={SIGN.height} />
      <FrameBar x={SIGN.width / 2} y={0} w={0.1} h={SIGN.height} />

      {/* Backlight wash bar — sits well BELOW the title so the glowing line and
          the lettering have clear breathing room (was nearly touching). */}
      <mesh position={[0, SIGN.height / 2 - 0.74, 0.01]}>
        <planeGeometry args={[SIGN.width - 0.4, 0.05]} />
        <meshStandardMaterial color={palette.gold1} emissive={palette.gold2} emissiveIntensity={2.4} toneMapped={false} />
      </mesh>

      {/* Title — elegant Cinzel caps, gold emissive troika text (blooms). troika
          needs a .ttf/.woff (NOT woff2); a load failure falls back to its SDF font. */}
      <Text
        font="/assets/fonts/Cinzel-Bold.ttf"
        position={[0, SIGN.height / 2 - 0.34, 0.02]}
        fontSize={0.42}
        letterSpacing={0.08}
        color={palette.gold1}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.012}
        outlineColor={palette.gold3}
      >
        HIGHEST ROLLERS
        <meshStandardMaterial
          attach="material"
          color={palette.gold1}
          emissive={palette.gold2}
          emissiveIntensity={1.8}
          metalness={1}
          roughness={0.25}
          toneMapped={false}
        />
      </Text>

      {/* Standings — cream Cinzel text, one line per leader (no bloom). */}
      {rows.length === 0 && (
        <Text font="/assets/fonts/Cinzel-Regular.ttf" position={[0, -0.1, 0.02]} fontSize={0.24} color={palette.cream} anchorX="center" anchorY="middle">
          No standings yet
        </Text>
      )}
      {rows.map((e, i) => {
        const y = SIGN.height / 2 - 1.06 - i * 0.38;
        return (
          <group key={`${e.name}-${i}`} position={[0, y, 0.02]}>
            <Text font="/assets/fonts/Cinzel-Regular.ttf" position={[-SIGN.width / 2 + 0.5, 0, 0]} fontSize={0.22} color={palette.gold2} anchorX="left" anchorY="middle">
              {`${i + 1}.`}
            </Text>
            <Text font="/assets/fonts/Cinzel-Regular.ttf" position={[-SIGN.width / 2 + 1.0, 0, 0]} fontSize={0.22} color={palette.cream} anchorX="left" anchorY="middle" maxWidth={3.4}>
              {e.name}
            </Text>
            <Text font="/assets/fonts/Cinzel-Regular.ttf" position={[SIGN.width / 2 - 0.5, 0, 0]} fontSize={0.22} color={palette.gold1} anchorX="right" anchorY="middle">
              {formatCents(e.balanceCents)}
            </Text>
          </group>
        );
      })}
    </group>
  );
}

/** One emissive gold frame bar. */
function FrameBar({ x, y, w, h }: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }) {
  return (
    <mesh position={[x, y, 0]}>
      <boxGeometry args={[w, h, 0.06]} />
      <meshStandardMaterial {...materials.gold} emissiveIntensity={1.6} toneMapped={false} />
    </mesh>
  );
}
